import { NextSeo } from 'next-seo';
import { TherapistSearchResponseDto } from '@app/api';
import styled from 'styled-components';
import { BreakpointsEnum, IFilter } from '@app/types';
import { GetServerSideProps } from 'next';
import {
  AllTherapists,
  ModalAfterRegistration,
  Sidebar,
} from '@app/pageComponents/AllTherapistsPage';
import withFilterLogic, {
  iLoadTherapistParamsFromPage,
} from '@app/hoc/withFilterLogic';
import {
  iGetWithFiltersResponse,
  iTherapistFilter,
} from '@app/backend/therapists/getTherapistsWithFilter';
import backend from '@app/backend';
import { AllTherapistsTab } from '@app/pageComponents/AllTherapistsPage/AllTherapists';
import {
  changeDiseaseUuidsToUrls,
  getDiseaseUrlByUuid,
} from '@app/pageComponents/AllTherapistsPage/AllTherapists/disease.redirects';
import isUUID from '@app/utils/isUUID';
import queryString from 'query-string';
import { useState } from 'react';
import { SidebarLayoutWide } from '@/app/layouts/SidebarLayoutWide';
import { getMethodUrlByUuid } from '@/app/pageComponents/AllTherapistsPage/AllTherapists/therapyMethods.redirects';
import { redirectBrokenUrls } from '@/app/utils/redirectBrokenUrls';
import { getServerSideUser } from '@app/auth';
import Script from 'next/script'
import { selectors } from '@/app/store';
import { useAppSelector } from '@/app/store/hooks';
import { COLORS } from '@/app/constants';

interface Props {
  currentUrl: string;
  initialFilters: IFilter[];
  tab: string | null;

  initialTherapists: Array<TherapistSearchResponseDto>;
  initialTotal: number;
  initialCount: number;
  initialTotalPage: number;
  initialPage: number;

  page: number;
  setPage: (p: number) => void;

  therapists: Array<TherapistSearchResponseDto>;
  setTherapists: (arr: Array<TherapistSearchResponseDto>) => void;
  loadTherapists: (
    p: iLoadTherapistParamsFromPage,
  ) => Promise<iGetWithFiltersResponse>;

  applyAndSaveAllSelectedFilters: (p?: number, childOld?: number, therapistOld?: string) => void;

  isLoadingPage: boolean;
  isMobile: boolean;
  hasMounted: boolean;
  shouldShowModal: boolean;
  isOpenInterviewModal: boolean;
  setIsOpenInterviewModal: (p: boolean) => void;
  isB2B: boolean;
}

const AllTherapistsPage = (props: Props) => {
  const {
    tab,
    currentUrl,
    initialFilters,
    initialTherapists,
    initialTotalPage,
    initialTotal,
    initialCount,

    page,
    setPage,

    setTherapists,
    loadTherapists,

    applyAndSaveAllSelectedFilters,

    isLoadingPage,
    isMobile,
    hasMounted,
    shouldShowModal,
    isOpenInterviewModal,
    setIsOpenInterviewModal,
    isB2B,
  } = props;

  const [therapistAge, setTherapistAge] = useState<string | undefined>(undefined);

  const isAuth = Boolean(useAppSelector(selectors.auth.selectToken));

  return (
    <>
      <NextSeo
        title="Все специалисты"
        description='Подберите психолога по вашему запросу или проблеме. Психологические консультации от 2 500 ₽. Проверенные специалисты с высшим образованием.'
        canonical="https://pogovorim.online/all-therapists/"
      />
      {!isAuth && <Script
        src="//code.jivo.ru/widget/zePApCl3Oj"
        async
        strategy="beforeInteractive"
        data-nscript="beforeInteractive"
      />}

      {!isLoadingPage && (
        <SidebarLayoutWide
          mainColor={COLORS.primary5}
          sideComponent={
            isMobile && hasMounted ? null : (
              <Sidebar
                initialFilters={initialFilters}
                applyAndSaveAllSelectedFilters={applyAndSaveAllSelectedFilters}
                therapistAge={therapistAge}
                setTherapistAge={setTherapistAge}
                therapistsPage={true}
              />
            )
          }
        >
          <AllTherapists
            defaultTab={tab}
            initialFilters={initialFilters}
            initialTotalPage={initialTotalPage}
            initialTotal={initialTotal}
            initialCount={initialCount}
            therapists={initialTherapists}
            setTherapists={setTherapists}
            loadTherapists={loadTherapists}
            page={page}
            setPage={setPage}
            currentUrl={currentUrl}
            applyAndSaveAllSelectedFilters={applyAndSaveAllSelectedFilters}
            therapistAge={therapistAge}
            setTherapistAge={setTherapistAge}
            showBookSessionButtons={!isB2B}
          />
        </SidebarLayoutWide>
      )}

      {shouldShowModal && (
        <StyledModalAfterRegistration
          isOpen={isOpenInterviewModal}
          onClose={() => setIsOpenInterviewModal(false)}
        />
      )}
    </>
  );
};

export default withFilterLogic(AllTherapistsPage);

export const config = {
  runtime: 'experimental-edge',
};

export const getServerSideProps: GetServerSideProps = async ({
  res,
  req,
  query,
}) => {
  const user = await getServerSideUser({ req });
  const isB2B = !!user?.metadata.isB2B;

  const currentUrl = req.url;

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59',
  );

  const redirect = redirectBrokenUrls(currentUrl);

  if (redirect) {
    return redirect;
  }

  const emptyData = {
    initialTherapists: [],
    initialTotal: 0,
    initialCount: 0,
    initialTotalPage: 1,
    initialFilters: [],
    initialPage: 1,
    tab: null,
    currentUrl,
    isB2B,
  };

  const isOnlyOneDiseaseSelected = typeof query?.diseases === 'string';
  const isOnlyOneMethodSelected = !isOnlyOneDiseaseSelected && typeof query?.therapyMethods === 'string';
  const handleSingleDisease = (diseaseId: string) => {
    const url = getDiseaseUrlByUuid(diseaseId);

    if (url) {
      return {
        redirect: {
          permanent: false,
          destination: `/all-therapists/${url}/`,
        },
      };
    }
    return { props: emptyData };
  };

  const handleMultipleDiseases = (diseases: string[]) => {
    const queryFilters = changeDiseaseUuidsToUrls({ ...query, diseases });
    const paramsAsString = queryString.stringify({ ...queryFilters });

    return {
      redirect: {
        permanent: false,
        destination: `/all-therapists/?${paramsAsString}`,
      },
    };
  };

  const handleSingleMethod = (methodId: string) => {
    const url = getMethodUrlByUuid(methodId);
    if (url) {
      const paramsAsString = queryString.stringify({ ...query, therapyMethods: undefined });
      return {
        redirect: {
          permanent: false,
          destination: `/all-therapists/${url}/?${paramsAsString}`,
        },
      };
    }
    return { props: emptyData };
  };


  if (isOnlyOneDiseaseSelected) {
    const diseaseId = query.diseases as string;
    if (isUUID(diseaseId)) {
      return handleSingleDisease(diseaseId);
    }
  } else if (query?.diseases?.length) {
    if (query.diseases.length === 1 && isUUID(query.diseases[0])) {
      return handleSingleDisease(query.diseases[0]);
    } else if (query.diseases.length > 1 && isUUID(query.diseases[0])) {
      return handleMultipleDiseases(query.diseases as string[]);
    }
  }

  if (isOnlyOneMethodSelected) {
    const methodId = query.therapyMethods as string;
    if (isUUID(methodId)) {
      return handleSingleMethod(methodId);
    }
  }

  const params = {
    ...query,
    firstname: query.search,
    lastname: query.search,
    dayLimit: ( query?.dayLimit && ( +query.dayLimit >= 1 && +query?.dayLimit <= 31 )) ? query.dayLimit : undefined,
    isRecommendedTab:
      query?.tab === AllTherapistsTab.recommended ? true : undefined,
    adultTherapists: true,
  };

  let filters = [];
  let data: iGetWithFiltersResponse | undefined = undefined;

  try {
    const [filtersFromDB, therapistsFromDB] = await Promise.all([
      backend.therapists.getFiltersContent(),
      backend.therapists.getWithFilters(params as iTherapistFilter),
    ]);

    if (!filtersFromDB) {
      throw 'NoFilters';
    }
    if (!therapistsFromDB) {
      throw 'NoData';
    }

    filters = filtersFromDB;
    data = therapistsFromDB;
  } catch (err) {
    return { props: emptyData };
  }

  return {
    props: {
      currentUrl,
      tab: query?.tab || null,
      initialTherapists: data.therapists,
      initialTotal: data.total ?? 0,
      initialCount: data.count ?? 0,
      initialTotalPage: data.totalPage ?? 0,
      initialFilters: filters,
      initialPage: data.page ?? 1,
      isB2B,
    },
  };
};

const StyledModalAfterRegistration = styled(ModalAfterRegistration)`
  /* Сделано для того, чтобы перебить специфичность стилей библиотеки */
  && {
    max-width: 440px;
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    && {
      max-width: 100%;
    }
  }
`;
